/**
 * ValidationRegister (C-7) — validation & qualification register.
 * KPI cards + type/status filters + table + "New validation" dialog.
 * Route: /validation
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import {
  useValidationsQuery, useCreateValidation, VAL_STATUS_LABEL, VAL_TYPE_LABEL, type NewValidationInput,
} from './useValidation';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}

export function ValidationRegister() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const q = useValidationsQuery({ status: status || undefined, type: type || undefined });
  const create = useCreateValidation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewValidationInput>({ title: '', validationType: 'PROCESS' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.title.trim()) { setErr('A title is required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ title: '', validationType: 'PROCESS' });
      navigate({ to: '/validation/$id', params: { id: created.validationId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not open the validation event.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Quality Events"
        title="Validation & Qualification"
        subtitle="Process, cleaning, equipment (IQ/OQ/PQ), computer system, HVAC, method & utility validation — protocol → execution → report → approval, with linked deviations & CAPAs."
        actions={
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>
            New validation
          </button>
        }
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.in_execution}</span><span className={styles.kpiLabel}>In execution</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.approved}</span><span className={styles.kpiLabel}>Approved</span></div>
          <div className={`${styles.kpi} ${Number(stats.overdue) > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Type filter" value={type} onChange={(e) => setType(e.target.value)} className={styles.select}>
          <option value="">All types</option>
          {Object.keys(VAL_TYPE_LABEL).map((t) => <option key={t} value={t}>{VAL_TYPE_LABEL[t]}</option>)}
        </select>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(VAL_STATUS_LABEL).map((s) => <option key={s} value={s}>{VAL_STATUS_LABEL[s]}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} event{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading validation events…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No validation events match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>Validation</th><th>Title</th><th>Type</th><th>System</th><th>Status</th><th>Target</th></tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.validationId} className={styles.row} onClick={() => navigate({ to: '/validation/$id', params: { id: v.validationId } })}>
                <td className={styles.mono}>{v.validationNumber}</td>
                <td>{v.title}{v.protocolNumber ? <span className={styles.sub}> · {v.protocolNumber}</span> : ''}</td>
                <td>{VAL_TYPE_LABEL[v.validationType] ?? v.validationType}</td>
                <td>{v.systemRef ?? '—'}</td>
                <td><span className={styles.statusPill}>{VAL_STATUS_LABEL[v.status] ?? v.status}</span></td>
                <td>{fmtDate(v.targetDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="New validation event">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Open a validation event</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Title *</span>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Process Validation — Atorvastatin 20mg Tablets" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Type</span>
                <select value={form.validationType} onChange={(e) => setForm({ ...form, validationType: e.target.value })}>
                  {Object.keys(VAL_TYPE_LABEL).map((t) => <option key={t} value={t}>{VAL_TYPE_LABEL[t]}</option>)}
                </select></label>
              <label className={styles.field}><span>Protocol number</span>
                <input type="text" value={form.protocolNumber ?? ''} onChange={(e) => setForm({ ...form, protocolNumber: e.target.value })} /></label>
            </div>
            <div className={styles.field2}>
              <label className={styles.field}><span>System / equipment / process</span>
                <input type="text" value={form.systemRef ?? ''} onChange={(e) => setForm({ ...form, systemRef: e.target.value })} placeholder="e.g. Compression Line 2" /></label>
              <label className={styles.field}><span>Target completion</span>
                <input type="date" value={form.targetDate ?? ''} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} /></label>
            </div>
            <label className={styles.field}><span>Approach</span>
              <input type="text" value={form.approach ?? ''} onChange={(e) => setForm({ ...form, approach: e.target.value })} placeholder="e.g. 3 consecutive batches · IQ/OQ/PQ · GAMP 5 risk-based" /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Opening…' : 'Open validation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
